import { matchSorter } from "match-sorter";
import type { PlaceFeature, PlaceGeoJSON, PlacePreviewFeature, PlaceType } from "../../lib/api";
import { suggestPlaceType } from "../../lib/mapboxUtils";
import type { SearchBoxSuggestion, SearchResultItem } from "./types";

/** Tolerance for coordinate matching (~10m at equator = 0.0001 degrees) */
const COORD_TOLERANCE = 0.0001;

/** Default radius for category-based place filtering */
const CATEGORY_PROXIMITY_KM = 50;

/** Check if a coordinate is within tolerance of any in the list */
export function isNearExistingPlace(coords: [number, number], existingCoords: [number, number][]): boolean {
  const [lng, lat] = coords;
  return existingCoords.some(([existingLng, existingLat]) => {
    return Math.abs(lng - existingLng) < COORD_TOLERANCE && Math.abs(lat - existingLat) < COORD_TOLERANCE;
  });
}

export function parseCoordinates(input: string): [number, number] | null {
  const match = input.trim().match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
  if (!match) return null;

  const lat = parseFloat(match[1]);
  const lng = parseFloat(match[2]);

  const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
  const isValidLng = !isNaN(lng) && lng >= -180 && lng <= 180;

  return isValidLat && isValidLng ? [lng, lat] : null;
}

export function suggestionToItem(suggestion: SearchBoxSuggestion): SearchResultItem {
  const isBrandOrCategory = suggestion.feature_type === "brand" || suggestion.feature_type === "category";
  return {
    id: suggestion.mapbox_id,
    name: suggestion.name,
    subtitle: isBrandOrCategory
      ? "See locations"
      : suggestion.place_formatted || suggestion.full_address || suggestion.address || "",
    placeType: suggestPlaceType({ maki: suggestion.maki }),
  };
}

/** Convert a local PlaceFeature to a SearchResultItem for the dropdown */
export function placeFeatureToItem(place: PlaceFeature): SearchResultItem {
  const props = place.properties;
  return {
    id: props.id,
    name: props.name,
    subtitle: props.description ?? "",
    placeType: props.type,
  };
}

/** Filter places by name using fuzzy matching */
export function filterPlacesByQuery(places: PlaceFeature[], query: string): PlaceFeature[] {
  if (!query.trim() || query.length < 2) return [];
  return matchSorter(places, query, {
    keys: ["properties.name"],
    threshold: matchSorter.rankings.CONTAINS,
  });
}

/** Filter places to exclude those in search results (by coordinate proximity) */
export function filterBySearchResults(places: PlaceGeoJSON, searchResults: PlacePreviewFeature[]): PlaceGeoJSON {
  if (searchResults.length === 0) return places;

  const searchCoords = searchResults.map((f) => f.geometry.coordinates as [number, number]);

  return {
    ...places,
    features: places.features.filter(
      (f) => !isNearExistingPlace(f.geometry.coordinates as [number, number], searchCoords),
    ),
  };
}

/** Approximate distance in km between two coordinates (Haversine formula) */
function haversineDistance(coord1: [number, number], coord2: [number, number]): number {
  const [lng1, lat1] = coord1;
  const [lng2, lat2] = coord2;
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Filter places by type and proximity to a center point */
export function filterPlacesByTypeAndProximity(
  places: PlaceFeature[],
  type: PlaceType,
  center: [number, number],
  maxDistanceKm = CATEGORY_PROXIMITY_KM,
): PlaceFeature[] {
  return places.filter((place) => {
    if (place.properties.type !== type) return false;
    const coords = place.geometry.coordinates as [number, number];
    return haversineDistance(coords, center) <= maxDistanceKm;
  });
}
